import React, { useState, useEffect } from 'react';
import { toast } from 'react-toast';
import '../../styles/ai-testcase-generator.css';
import axiosInstance from '../../api/axiosInstance';
import axios from 'axios';

// const API_BASE_URL = 'http://3.139.107.45:8001';

const AITestCaseGenerator = ({ item, itemType, onClose, onSave, isBatchGeneration = false, projectId }) => {
  const [generatedTestCases, setGeneratedTestCases] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationComplete, setGenerationComplete] = useState(false);
  const [selectedTestCases, setSelectedTestCases] = useState([]);
  const [savingStatus, setSavingStatus] = useState(null);
  const [editingTestCase, setEditingTestCase] = useState(null);
  const [openTestCaseId, setOpenTestCaseId] = useState(null); // single-open collapsible
  const [editedContent, setEditedContent] = useState('');
  const [error, setError] = useState(null);
 
  useEffect(() => {
    const fetchTestCases = async () => {
      try {
        setIsGenerating(true);
        setError(null);
        
        const epicKey = itemType === 'epic' ? item.key : item.key;
        if (!epicKey) {
          throw new Error('Epic key not found for this item');
        }

        const response = await axiosInstance.post(
          `/jira_data/test_case/${projectId}/${epicKey}`,
          {
            project_id: projectId,
            epic_key: epicKey
          }
        );

        if (response.status_code === 200) {
          const testCasesData = JSON.parse(response.test_cases.trim());
          const formattedTestCases = testCasesData.test_cases.map((tc, index) => ({
            id: index + 1,
            title: tc.test_name,
            description: tc.description,
            steps: tc.steps,
            expectedResults: tc.expected_results,
            priority: tc.priority,
            prerequisites: tc.prerequisites,
            estimatedTime: getEstimatedTime(tc.priority)
          }));

          setGeneratedTestCases(formattedTestCases);
          setSelectedTestCases(formattedTestCases.map(tc => tc.id));
          setGenerationComplete(true);
          // Open first test case by default
          if (formattedTestCases && formattedTestCases.length > 0) {
            setOpenTestCaseId(`test-${formattedTestCases[0].id}`);
          }
        } else {
          throw new Error(response.message || 'Failed to generate test cases');
        }
      } catch (err) {
        console.error('Test case generation error:', err);
        setError(err.message);
      } finally {
        setIsGenerating(false);
      }
    };

    fetchTestCases();
  }, [item, itemType, projectId]);

  const getEstimatedTime = (priority) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return '60 minutes';
      case 'medium':
        return '45 minutes';
      case 'low':
        return '30 minutes';
      default:
        return '30 minutes';
    }
  };

  // Handle test case selection
  const handleTestCaseSelection = (testCaseId) => {
    if (selectedTestCases.includes(testCaseId)) {
      setSelectedTestCases(selectedTestCases.filter(id => id !== testCaseId));
    } else {
      setSelectedTestCases([...selectedTestCases, testCaseId]);
    }
  };

  // Handle select all test cases
  const handleSelectAll = () => {
    if (selectedTestCases.length === generatedTestCases.length) {
      setSelectedTestCases([]);
    } else {
      setSelectedTestCases(generatedTestCases.map(tc => tc.id));
    }
  };

  // Handle saving test cases
  const handleSaveTestCases = () => {
    setSavingStatus('saving');
    // Get only the selected test cases
    const testCasesToSave = generatedTestCases.filter(tc =>
      selectedTestCases.includes(tc.id)
    );
    // Simulate saving with a delay
    setTimeout(() => {
      onSave(testCasesToSave, item, itemType);
      setSavingStatus('saved');
      // Reset status after a delay
      setTimeout(() => {
        setSavingStatus(null);
      }, 2000);
    }, 1500);
  };

  // Handle editing a test case
  const handleEditTestCase = (testCase) => {
    setEditingTestCase(testCase);
    setEditedContent(JSON.stringify(testCase, null, 2));
    // open the test case being edited
    setOpenTestCaseId(`test-${testCase.id}`);
  };

  const handleToggleTestCaseOpen = (testCaseId) => {
    setOpenTestCaseId(prev => (prev === testCaseId ? null : testCaseId));
  };

  // Handle saving edited test case
  const handleSaveEdit = () => {
    try {
      const updatedTestCase = JSON.parse(editedContent);
      // Update the test case in the generated test cases
      const updatedTestCases = generatedTestCases.map(tc =>
        tc.id === editingTestCase.id ? updatedTestCase : tc
      );
      setGeneratedTestCases(updatedTestCases);
      setEditingTestCase(null);
    } catch (error) {
      toast.error('Invalid JSON format. Please check your edits.');
    }
  };

  // Handle canceling edit
  const handleCancelEdit = () => {
    setEditingTestCase(null);
  };

  // Render loading spinner during generation
  const renderLoadingSpinner = () => (
    <div className="loading-container">
      <div className="spinner"></div>
      <p>Generating test cases using AI...</p>
      <p className="loading-subtext">Analyzing requirements, context, and best practices...</p>
    </div>
  );

  // Render test case card
  const renderTestCase = (testCase) => (
    <div
      key={testCase.id}
      className={`test-case-card ${selectedTestCases.includes(testCase.id) ? 'selected' : ''} ${openTestCaseId === `test-${testCase.id}` ? 'open' : 'collapsed'}`}
    >
      <div className="test-case-header" onClick={() => handleToggleTestCaseOpen(`test-${testCase.id}`)} style={{cursor: 'pointer'}}>
        <div className="test-case-selection">
          <input
            type="checkbox"
            checked={selectedTestCases.includes(testCase.id)}
            onChange={(e) => { e.stopPropagation(); handleTestCaseSelection(testCase.id); }}
            onClick={(e) => e.stopPropagation()}
            id={`test-case-${testCase.id}`}
          />
          <label htmlFor={`test-case-${testCase.id}`} className="test-case-title">
            {testCase.title}
          </label>
        </div>
        <div className="test-case-actions">
          <button
            className="edit-test-case-btn"
            onClick={(e) => { e.stopPropagation(); handleEditTestCase(testCase); }}
            title="Edit test case"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </button>
        </div>
      </div>

      <div className="test-case-meta">
        <span className={`priority-badge priority-${testCase.priority.toLowerCase()}`}>
          {testCase.priority}
        </span>
        <span className="time-estimate">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
          {testCase.estimatedTime}
        </span>
      </div>
      {openTestCaseId === `test-${testCase.id}` ? (
        <>
          <div className="test-case-description">
            {testCase.description}
          </div>

          <div className="test-case-content">
            <div className="test-case-section">
              <h4>Steps:</h4>
              <ol>
                {testCase.steps.map((step, index) => (
                  <li key={index}>{step}</li>
                ))}
              </ol>
            </div>

            <div className="test-case-section">
              <h4>Expected Results:</h4>
              <ol>
                {testCase.expectedResults.map((result, index) => (
                  <li key={index}>{result}</li>
                ))}
              </ol>
            </div>

            {testCase.prerequisites && (
              <div className="test-case-section">
                <h4>Prerequisites:</h4>
                <p>{testCase.prerequisites}</p>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="collapsed-summary">
          <span className={`priority-badge small priority-${testCase.priority.toLowerCase()}`}>{testCase.priority}</span>
          <span className="time-estimate small">{testCase.estimatedTime}</span>
          <div className="short-description">{testCase.description && testCase.description.length > 140 ? testCase.description.substring(0, 137) + '...' : testCase.description}</div>
        </div>
      )}
    </div>
  );

  // Render test case editor
  const renderTestCaseEditor = () => (
    <div className="test-case-editor-overlay">
      <div className="test-case-editor">
        <div className="editor-header">
          <h3>Edit Test Case</h3>
          <button className="close-editor-btn" onClick={handleCancelEdit}>×</button>
        </div>
        <div className="editor-content">
          <textarea
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            className="editor-textarea"
          />
        </div>
        <div className="editor-footer">
          <button className="cancel-btn" onClick={handleCancelEdit}>Cancel</button>
          <button className="save-btn" onClick={handleSaveEdit}>Save Changes</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="ai-testcase-generator-overlay rt-scenario-page">
      <div className="ai-testcase-generator-container rt-scenario-panel">
        <header className="rt-scenario-chrome">
          <div className="rt-scenario-chrome__nav">
            <button
              type="button"
              className="rt-btn rt-btn--secondary rt-btn--sm"
              onClick={onClose}
              title="Back to Jira items"
            >
              ← Back to Jira
            </button>
            <span className="rt-scenario-chrome__crumb">
              Requirements / Jira / Test Cases
            </span>
          </div>
          <div className="rt-scenario-chrome__title-row">
            <div>
              <div className="rt-scenario-chrome__badges">
                <span className={`rt-badge ${itemType === 'epic' ? 'rt-badge--info' : 'rt-badge--neutral'}`}>
                  {itemType === 'epic' ? 'Epic' : 'Story'}
                </span>
                <span className="rt-badge rt-badge--info">AI Test Cases</span>
              </div>
              <h2 className="rt-scenario-chrome__title">
                {item?.key}: {item?.summary}
              </h2>
            </div>
          </div>
          {item?.description ? (
            <p className="rt-scenario-chrome__desc">{item.description}</p>
          ) : null}
        </header>

        {error && (
          <div className="error-message" style={{ margin: '12px 20px 0' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            {error}
          </div>
        )}

        <div className="generator-content">
          {isGenerating ? (
            renderLoadingSpinner()
          ) : (
            <>
              <div className="test-cases-header">
                <div className="test-cases-title">
                  <h3>Generated Test Cases ({generatedTestCases.length})</h3>
                </div>

                {generatedTestCases.length > 0 && (
                  <div className="test-cases-actions">
                    <div className="select-all-container">
                      <input
                        type="checkbox"
                        id="select-all"
                        checked={selectedTestCases.length === generatedTestCases.length && generatedTestCases.length > 0}
                        onChange={handleSelectAll}
                      />
                      <label htmlFor="select-all">Select All </label>
                    </div>

                    <button
                      className={`save-btn ${savingStatus ? savingStatus : ''}`}
                      onClick={handleSaveTestCases}
                      disabled={selectedTestCases.length === 0 || savingStatus === 'saving'}
                    >
                      {savingStatus === 'saving' ? (
                        <>
                          <div className="btn-spinner"></div>
                          Saving...
                        </>
                      ) : savingStatus === 'saved' ? (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6L9 17l-5-5"></path>
                          </svg>
                          Saved!
                        </>
                      ) : (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                            <polyline points="17 21 17 13 7 13 7 21"></polyline>
                            <polyline points="7 3 7 8 15 8"></polyline>
                          </svg>
                          {isBatchGeneration ? 'Continue to Next Item' : `Save Selected (${selectedTestCases.length})`}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              <div className="test-cases-list">
                {generatedTestCases.length > 0 ? (
                  generatedTestCases.map(testCase => renderTestCase(testCase))
                ) : (
                  <div className="no-test-cases">
                    {error ? (
                      <p>Failed to generate test cases. Please try again later.</p>
                    ) : (
                      <p>No test cases have been generated yet.</p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {editingTestCase && renderTestCaseEditor()}
    </div>
  );
};

export default AITestCaseGenerator;